import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Bookmark, ChevronRight } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";

/**
 * Polished layout for /legal/* and /trust/* pages.
 *
 * Design language matches /methodology — version pill + status pill,
 * hero band, multi-stat row, sticky table-of-contents sidebar, and
 * an icon-led prose column with cyan accents.
 *
 * Props:
 *   - eyebrow:      small uppercase kicker label (e.g. "LEGAL", "SECURITY")
 *   - title:        H1 title
 *   - lastUpdated:  human date string (e.g. "February 2026")
 *   - version:      optional version pill text (e.g. "qortrace-legal-v1")
 *   - status:       optional status pill text (e.g. "ACTIVE", "DRAFT")
 *   - intro:        optional intro paragraph (rendered above body, below stats)
 *   - stats:        optional [{ value, label }] tiles in the hero
 *   - pillars:      optional [{ icon, title, desc, accent }] cards rendered right under the hero
 *   - sections:     optional [{ id, label, icon? }] for the sticky ToC sidebar
 *   - children:     prose body (use <h2 id="..."> matching ToC ids for jump links)
 */
export default function LegalLayout({
  eyebrow,
  title,
  lastUpdated,
  version,
  status,
  intro,
  stats,
  pillars,
  sections,
  children,
}) {
  const { hash } = useLocation();
  const [activeId, setActiveId] = useState(null);

  // Smooth-scroll to hash on first paint.
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  // IntersectionObserver to highlight the current ToC section.
  useEffect(() => {
    if (!sections || !sections.length) return;
    const els = sections
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -65% 0px", threshold: 0 },
    );
    els.forEach((e) => obs.observe(e));
    return () => obs.disconnect();
  }, [sections]);

  return (
    <div className="min-h-screen flex flex-col qt-radial-bg">
      <Header />
      <main className="flex-1 pt-12 pb-20" data-testid="legal-page">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8">
          {/* ----- Hero band ----- */}
          <div data-testid="legal-hero" className="pt-2">
            <div className="flex items-center gap-3 flex-wrap">
              {version && (
                <span
                  data-testid="legal-version-pill"
                  className="qt-mono text-[10px] px-2.5 py-1 rounded"
                  style={{
                    color: "var(--qt-cyan)",
                    border: "1px solid rgba(0,229,255,0.4)",
                    background: "rgba(0,229,255,0.08)",
                    letterSpacing: "0.16em",
                    fontWeight: 700,
                  }}
                >
                  {version}
                </span>
              )}
              {status && (
                <span
                  data-testid="legal-status-pill"
                  className="qt-mono text-[10px] px-2.5 py-1 rounded inline-flex items-center gap-1.5"
                  style={{
                    color: "#1FDD83",
                    border: "1px solid rgba(123,217,168,0.4)",
                    background: "rgba(123,217,168,0.08)",
                    letterSpacing: "0.16em",
                    fontWeight: 700,
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: 999, background: "#1FDD83", display: "inline-block" }} />
                  {status}
                </span>
              )}
              <span
                className="qt-mono text-[11px]"
                style={{ color: "var(--qt-text-3)", letterSpacing: "0.06em" }}
                data-testid="legal-eyebrow"
              >
                {eyebrow} · LAST UPDATED {String(lastUpdated || "").toUpperCase()}
              </span>
            </div>

            <h1
              className="qt-h2 text-4xl sm:text-5xl mt-4"
              data-testid="legal-title"
              style={{ color: "#fff", letterSpacing: "-0.02em", lineHeight: 1.05 }}
            >
              {title}
            </h1>

            {intro && (
              <p
                className="mt-4 max-w-3xl text-base"
                style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}
                data-testid="legal-intro"
              >
                {intro}
              </p>
            )}

            {stats && stats.length > 0 && (
              <div
                className="mt-6 flex items-center gap-x-6 gap-y-3 flex-wrap"
                data-testid="legal-stats"
              >
                {stats.map((s, i) => (
                  <div key={s.label} className="flex items-center gap-2">
                    <span
                      className="qt-mono text-2xl font-bold"
                      style={{ color: "var(--qt-cyan)", letterSpacing: "-0.02em" }}
                    >
                      {s.value}
                    </span>
                    <span
                      className="qt-mono text-[10px]"
                      style={{ color: "var(--qt-text-3)", letterSpacing: "0.16em" }}
                    >
                      {s.label}
                    </span>
                    {i < stats.length - 1 && (
                      <span
                        className="qt-mono text-base"
                        style={{ color: "var(--qt-border)", marginLeft: 12 }}
                      >
                        ·
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ----- Pillars row ----- */}
          {pillars && pillars.length > 0 && (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-10"
              data-testid="legal-pillars"
            >
              {pillars.map(({ icon: Icon, title: t, desc, accent }) => (
                <div
                  key={t}
                  data-testid={`legal-pillar-${(t || "").toLowerCase().replace(/\s+/g, "-")}`}
                  className="qt-card"
                  style={{
                    padding: 18,
                    background: "rgba(4,9,20,0.5)",
                    borderColor: "var(--qt-border-soft)",
                  }}
                >
                  {Icon && <Icon size={18} style={{ color: accent || "var(--qt-cyan)" }} />}
                  <h3
                    className="qt-mono mt-3"
                    style={{ color: "#fff", fontWeight: 700, fontSize: 14, letterSpacing: "-0.01em" }}
                  >
                    {t}
                  </h3>
                  <p
                    className="text-[13px] mt-2"
                    style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}
                  >
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* ----- Body + ToC -----
              Iter273.3 — Beta-tester P1: at iPad portrait (768×1024, md but
              not lg) the old `gridTemplateColumns: "240px 1fr"` reserved a
              240px gutter for the ToC even though the aside is `hidden
              lg:block`. Result: prose was compressed and looked broken.
              Switched to Tailwind responsive grid so the 2-col layout only
              kicks in at lg+ where the aside is actually rendered. */}
          <div className={`mt-12 grid gap-10 ${sections && sections.length ? "lg:grid-cols-[240px_minmax(0,1fr)]" : ""}`}>
            {sections && sections.length > 0 && (
              <aside
                className="hidden lg:block"
                data-testid="legal-toc"
                style={{ position: "relative" }}
              >
                <div style={{ position: "sticky", top: 96 }}>
                  <div
                    className="qt-mono text-[10px] mb-3 flex items-center gap-2"
                    style={{ color: "var(--qt-text-3)", letterSpacing: "0.16em" }}
                  >
                    <Bookmark size={11} /> ON THIS PAGE
                  </div>
                  <nav className="flex flex-col gap-1.5">
                    {sections.map((s) => {
                      const active = activeId === s.id;
                      const Ic = s.icon;
                      return (
                        <a
                          key={s.id}
                          href={`#${s.id}`}
                          data-testid={`legal-toc-${s.id}`}
                          className="qt-mono text-[12px] flex items-center gap-2 transition-colors"
                          style={{
                            padding: "5px 8px",
                            borderLeft: `2px solid ${active ? "var(--qt-cyan)" : "transparent"}`,
                            color: active ? "#fff" : "var(--qt-text-2)",
                            background: active ? "rgba(0,229,255,0.06)" : "transparent",
                            textDecoration: "none",
                            letterSpacing: "0.01em",
                            lineHeight: 1.4,
                          }}
                        >
                          {Ic ? <Ic size={11} style={{ color: active ? "var(--qt-cyan)" : "var(--qt-text-3)" }} /> : <ChevronRight size={11} style={{ color: active ? "var(--qt-cyan)" : "var(--qt-text-3)" }} />}
                          <span>{s.label}</span>
                        </a>
                      );
                    })}
                  </nav>
                </div>
              </aside>
            )}

            <article className="qt-legal-prose max-w-[820px]" data-testid="legal-body">
              {children}

              {/* Footer-style cross-links to the rest of the legal cluster. */}
              <div
                className="mt-16 pt-6 grid grid-cols-2 sm:grid-cols-4 gap-3"
                style={{ borderTop: "1px solid var(--qt-border)" }}
                data-testid="legal-cross-links"
              >
                <Link to="/legal/privacy"  className="qt-legal-cross">Privacy Policy</Link>
                <Link to="/legal/terms"    className="qt-legal-cross">Terms of Service</Link>
                <Link to="/legal/cookies"  className="qt-legal-cross">Cookie Policy</Link>
                <Link to="/legal/security" className="qt-legal-cross">Security &amp; Disclosure</Link>
              </div>
            </article>
          </div>
        </div>
      </main>
      <Footer />

      <style>{`
        .qt-legal-prose,
        .qt-legal-prose p,
        .qt-legal-prose li {
          color: rgba(230, 239, 255, 0.78);
          font-size: 15.5px;
          line-height: 1.78;
        }
        .qt-legal-prose h2 {
          color: #FFFFFF;
          font-size: 24px;
          font-weight: 700;
          margin-top: 3rem;
          margin-bottom: 1rem;
          letter-spacing: -0.01em;
          padding-top: 1rem;
          border-top: 1px solid var(--qt-border);
          scroll-margin-top: 96px;
        }
        .qt-legal-prose h2:first-child { border-top: none; padding-top: 0; margin-top: 0; }
        .qt-legal-prose h3 {
          color: #FFFFFF;
          font-size: 17px;
          font-weight: 600;
          margin-top: 1.8rem;
          margin-bottom: 0.6rem;
          letter-spacing: -0.005em;
        }
        .qt-legal-prose p { margin-bottom: 1.1rem; }
        .qt-legal-prose ul {
          list-style: none;
          padding-left: 0;
          margin-bottom: 1.2rem;
        }
        .qt-legal-prose ul li {
          margin-bottom: 0.6rem;
          padding-left: 1.4rem;
          position: relative;
        }
        .qt-legal-prose ul li::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0.85em;
          width: 6px;
          height: 1px;
          background: var(--qt-cyan);
        }
        .qt-legal-prose a {
          color: #7AE7F2;
          text-decoration: underline;
          text-underline-offset: 3px;
          text-decoration-thickness: 1px;
          transition: color 120ms;
        }
        .qt-legal-prose a:hover { color: #FFFFFF; }
        .qt-legal-prose strong { color: #FFFFFF; }
        .qt-legal-prose em { color: rgba(255,255,255,0.92); font-style: italic; }
        .qt-legal-prose code {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          background: rgba(122,231,242,0.08);
          color: #7AE7F2;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 0.88em;
          border: 1px solid rgba(122,231,242,0.18);
        }
        .qt-legal-callout {
          margin: 1.5rem 0;
          padding: 14px 18px;
          border: 1px solid rgba(0,229,255,0.25);
          background: linear-gradient(180deg, rgba(0,229,255,0.06) 0%, rgba(0,229,255,0.02) 100%);
          border-radius: 8px;
          font-size: 14px;
          color: rgba(230, 239, 255, 0.92);
        }
        .qt-legal-callout-amber {
          border-color: rgba(255,176,0,0.28);
          background: linear-gradient(180deg, rgba(255,176,0,0.06) 0%, rgba(255,176,0,0.02) 100%);
        }
        .qt-legal-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 13.5px;
          margin: 1.2rem 0 1.6rem;
          border: 1px solid var(--qt-border);
          border-radius: 8px;
          overflow: hidden;
        }
        .qt-legal-table th,
        .qt-legal-table td {
          padding: 10px 14px;
          text-align: left;
          vertical-align: top;
          border-bottom: 1px solid var(--qt-border);
        }
        .qt-legal-table tr:last-child td { border-bottom: none; }
        .qt-legal-table th {
          background: rgba(0,229,255,0.06);
          color: #FFFFFF;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          font-size: 11px;
        }
        .qt-legal-table td { color: rgba(230, 239, 255, 0.78); }
        .qt-legal-cross {
          padding: 10px 12px;
          border: 1px solid var(--qt-border);
          border-radius: 6px;
          color: var(--qt-text-2);
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 11.5px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-decoration: none;
          transition: border-color 120ms, color 120ms, background 120ms;
        }
        .qt-legal-cross:hover {
          border-color: var(--qt-cyan);
          color: #fff;
          background: rgba(0,229,255,0.06);
        }
      `}</style>
    </div>
  );
}
